/**
 * plugin-manifests.ts
 *
 * Canonical Core Plugin Manifest Catalog & Capability Specifications:
 * - Declares the active plugin catalog for the Sovereign Mesh.
 * - Risk tiers: 'low' | 'medium' | 'high' | 'regulated'.
 * - Explicit required and optional dependencies with human-readable rationales.
 * - Capability definitions with configuration requirements and regulatory flags.
 * - Pinned versioning and SHA-256 manifest integrity hashes.
 */

export type PluginCategory =
  'workflow' | 'knowledge' | 'commerce' | 'logistics' | 'fiscal' | 'hardware';

export type PluginType =
  'primary_channel' | 'header_tool' | 'dock_widget' | 'background_service';

export type PluginRiskLevel = 'low' | 'medium' | 'high' | 'regulated';

export type CapabilityReadinessState =
  'ready' | 'not_configured' | 'degraded' | 'blocked';

export interface PluginCapabilityDefinition {
  readonly capabilityId: string;
  readonly displayName: string;
  readonly description: string;
  readonly requiresConfiguration: boolean;
  readonly isRegulated: boolean;
}

export interface SetupStep {
  readonly stepId: string;
  readonly label: string;
  readonly description: string;
  readonly required: boolean;
  readonly completedWhen: string;
}

export interface PluginManifest {
  readonly pluginId: string;
  readonly version: string;
  readonly displayName: string;
  readonly description: string;
  readonly category: PluginCategory;
  readonly pluginType?: PluginType;
  readonly riskLevel: PluginRiskLevel;
  readonly requiredDependencies: readonly string[];
  readonly optionalDependencies: readonly string[];
  readonly conflictsWith: readonly string[];
  readonly capabilities: readonly PluginCapabilityDefinition[];
  readonly setupSteps: readonly SetupStep[];
  readonly dataSchemas: readonly string[];
  readonly manifestHash: string;
}

export const CANONICAL_18_PLUGIN_CATALOG: readonly PluginManifest[] = [
  {
    pluginId: 'ChatPlugin',
    version: '1.0.0',
    displayName: 'Encrypted Chat & Channels',
    description: 'P2P encrypted messaging, group threads, and media sharing.',
    category: 'workflow',
    riskLevel: 'low',
    requiredDependencies: [],
    optionalDependencies: ['NotificationsPlugin'],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'chat.send',
        displayName: 'Send Messages',
        description: 'Post P2P encrypted messages',
        requiresConfiguration: false,
        isRegulated: false,
      },
      {
        capabilityId: 'chat.thread',
        displayName: 'Thread Management',
        description: 'Create and archive group threads',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['chat.v1'],
    manifestHash: 'sha256-chat-v1.0.0',
  },
  {
    pluginId: 'WhiteboardPlugin',
    version: '1.0.0',
    displayName: 'Collaborative Whiteboard',
    description:
      'Real-time vector drawing, spatial canvas, and visual mind-mapping.',
    category: 'workflow',
    riskLevel: 'low',
    requiredDependencies: [],
    optionalDependencies: [],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'whiteboard.draw',
        displayName: 'Vector Drawing',
        description: 'Render and broadcast canvas strokes',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['whiteboard.v1'],
    manifestHash: 'sha256-whiteboard-v1.0.0',
  },
  {
    pluginId: 'ActionPlannerPlugin',
    version: '1.0.0',
    displayName: 'Action Planner & Kanban',
    description:
      'Hierarchical Work Breakdown Structure (WBS), Kanban boards, and LexoRank sorting.',
    category: 'workflow',
    riskLevel: 'low',
    requiredDependencies: [],
    optionalDependencies: ['NotificationsPlugin'],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'task.create',
        displayName: 'Create Tasks',
        description: 'Create and assign Kanban WorkItems',
        requiresConfiguration: false,
        isRegulated: false,
      },
      {
        capabilityId: 'task.move',
        displayName: 'Move Tasks',
        description: 'Update status and LexoRank positions',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [
      {
        stepId: 'configure-workweek',
        label: 'Work Week Schedule',
        description: 'Set working hours and calendar constraints',
        required: false,
        completedWhen: 'planner.workweekConfigured',
      },
    ],
    dataSchemas: ['planner.v2'],
    manifestHash: 'sha256-actionplanner-v1.0.0',
  },
  {
    pluginId: 'ScrumMasterPlugin',
    version: '1.0.0',
    displayName: 'Scrum & Agile Master',
    description:
      'Sprint planning, automated daily standups, and blocker escalation.',
    category: 'workflow',
    riskLevel: 'low',
    requiredDependencies: ['ActionPlannerPlugin'],
    optionalDependencies: ['NotificationsPlugin', 'LLMWikiPlugin'],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'sprint.plan',
        displayName: 'Sprint Planning',
        description: 'Manage sprint velocity and milestones',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['scrum.v1'],
    manifestHash: 'sha256-scrummaster-v1.0.0',
  },
  {
    pluginId: 'NotificationsPlugin',
    version: '1.0.0',
    displayName: 'System Notifications Bus',
    description:
      'Unified cross-plugin event dispatch, desktop push, and audio chimes.',
    category: 'workflow',
    riskLevel: 'low',
    requiredDependencies: [],
    optionalDependencies: [],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'notify.broadcast',
        displayName: 'Event Broadcast',
        description: 'Publish system-wide notifications',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['notifications.v1'],
    manifestHash: 'sha256-notifications-v1.0.0',
  },
  {
    pluginId: 'WebRtcPlugin',
    version: '1.0.0',
    displayName: 'WebRTC Mesh Calling',
    description:
      'Decentralized voice/video rooms, screen sharing, and media bridging.',
    category: 'workflow',
    riskLevel: 'medium',
    requiredDependencies: [],
    optionalDependencies: ['NotificationsPlugin'],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'webrtc.call',
        displayName: 'Audio/Video Calling',
        description: 'Initiate P2P media connections',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['webrtc.v1'],
    manifestHash: 'sha256-webrtc-v1.0.0',
  },
  {
    pluginId: 'CrmPlugin',
    version: '1.0.0',
    displayName: 'CRM & Pipeline Hub',
    description:
      'Lead tracking, deal stage pipelines, contact management, and customer history.',
    category: 'workflow',
    riskLevel: 'low',
    requiredDependencies: ['ActionPlannerPlugin'],
    optionalDependencies: ['ChatPlugin'],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'crm.records',
        displayName: 'CRM Records',
        description: 'Manage accounts and contact records',
        requiresConfiguration: false,
        isRegulated: false,
      },
      {
        capabilityId: 'crm.proposals',
        displayName: 'Deal Proposals',
        description: 'Draft and review deal proposals',
        requiresConfiguration: false,
        isRegulated: false,
      },
      {
        capabilityId: 'crm.followups',
        displayName: 'Automated Follow-ups',
        description: 'Generate action planner follow-up tasks',
        requiresConfiguration: false,
        isRegulated: false,
      },
      {
        capabilityId: 'crm.deal.manage',
        displayName: 'Deal Pipeline',
        description: 'Track commercial opportunities across stages',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [
      {
        stepId: 'configure-pipeline',
        label: 'Pipeline Stages',
        description: 'Define deal stages and qualifying criteria',
        required: true,
        completedWhen: 'crm.pipelineConfigured',
      },
    ],
    dataSchemas: ['crm.v4'],
    manifestHash: 'sha256-crm-v1.0.0',
  },
  {
    pluginId: 'LLMWikiPlugin',
    version: '1.0.0',
    displayName: 'Sovereign LLMWiki',
    description:
      'Cryptographically anchored knowledge base and Architectural Decision Records (ADRs).',
    category: 'knowledge',
    riskLevel: 'low',
    requiredDependencies: [],
    optionalDependencies: ['RagKnowledgePlugin'],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'wiki.claim.create',
        displayName: 'Author Claims',
        description: 'Create verifiable wiki claims and ADRs',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [
      {
        stepId: 'configure-sources',
        label: 'Knowledge Sources',
        description: 'Specify document formats and review policies',
        required: false,
        completedWhen: 'wiki.sourcesConfigured',
      },
    ],
    dataSchemas: ['wiki.v1'],
    manifestHash: 'sha256-llmwiki-v1.0.0',
  },
  {
    pluginId: 'RagKnowledgePlugin',
    version: '1.0.0',
    displayName: 'RAG Knowledge Indexer',
    description:
      'Vector embeddings, semantic cosine retrieval, and multi-tenant document indexing.',
    category: 'knowledge',
    riskLevel: 'low',
    requiredDependencies: [],
    optionalDependencies: [],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'rag.search',
        displayName: 'Semantic Search',
        description: 'Execute semantic vector similarity searches',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['rag.v1'],
    manifestHash: 'sha256-rag-v1.0.0',
  },
  {
    pluginId: 'BuzzAcpPlugin',
    version: '1.0.0',
    displayName: 'Nostr ACP v0.4 Protocol',
    description:
      'Agent Communication Protocol over Nostr relays with cryptographic event signing.',
    category: 'knowledge',
    riskLevel: 'medium',
    requiredDependencies: [],
    optionalDependencies: [],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'acp.wire.encode',
        displayName: 'Wire Protocol',
        description: 'Encode and sign Nostr ACP events',
        requiresConfiguration: false,
        isRegulated: false,
      },
    ],
    setupSteps: [],
    dataSchemas: ['buzz.v1'],
    manifestHash: 'sha256-buzzacp-v1.0.0',
  },
  {
    pluginId: 'FaceIdDoorOpenerPlugin',
    version: '1.0.0',
    displayName: 'FaceID Door Opener',
    description: 'Hardware‑controlled door access with dual‑factor FaceID and optional mobile fallback.',
    category: 'hardware',
    pluginType: 'background_service',
    riskLevel: 'high',
    requiredDependencies: [],
    optionalDependencies: [],
    conflictsWith: [],
    capabilities: [
      {
        capabilityId: 'access.door.relay',
        displayName: 'Door Relay Control',
        description: 'Pulse the door lock relay (hold 3 s).',
        requiresConfiguration: true,
        isRegulated: true,
      },
      {
        capabilityId: 'camera.video.ingest',
        displayName: 'Camera Video Ingestion',
        description: 'Capture and process video streams for FaceID.',
        requiresConfiguration: true,
        isRegulated: false,
      },
      {
        capabilityId: 'biometric.faceid.matching',
        displayName: 'FaceID Matching',
        description: 'Perform facial recognition for access.',
        requiresConfiguration: false,
        isRegulated: true,
      },
    ],
    setupSteps: [
      {
        stepId: 'configure_camera_streams',
        label: 'Configure Ingress/Egress Streams',
        description: 'Set RTSP/YsxLite endpoints for CAM_EXT and CAM_INT.',
        required: true,
        completedWhen: 'config.cameras.ext && config.cameras.int',
      },
      {
        stepId: 'configure_relay_hardware',
        label: 'Assign Door Relay Interface',
        description: 'Select GPIO pin, USB relay port, or IP webhook trigger.',
        required: true,
        completedWhen: 'config.relay.type && config.relay.target',
      },
    ],
    dataSchemas: ['dooropener.v1'],
    manifestHash: 'sha256-dooropener-v1.0.0',
  },
];

/**
 * Helper to retrieve canonical identifier for a plugin manifest.
 */
export function getManifestId(manifest: PluginManifest | Record<string, any>): string {
  return (manifest as any).id || (manifest as any).name || '';
}

/**
 * Key-value mapping of plugin manifests by ID for fast lookup in plugin-registry.
 */
export const PLUGIN_MANIFESTS: Record<string, PluginManifest> = Object.fromEntries(
  CANONICAL_18_PLUGIN_CATALOG.map((manifest) => [getManifestId(manifest), manifest])
);
